import axios, {Method} from 'axios'
import _ from 'lodash'
import {EntityAttributes, ET_BOOKING, ET_PERSURL} from 'module/BSEntry/EntryTypeParams'
import {FetchMode} from 'module/BSEntry/enum/FetchMode';
import {getAppName} from 'util/AppName';
import {SisNameAppSpecificUrl, SisNameUrl} from 'util/SisNameUrl';
import {BSApiAction} from 'module/BSEntry/enum/BSApiAction';
import {alertError, alertServerError, confirmJN} from 'module/ConfirmDialog/component/ConfirmDialogContainer';
import {CFMember} from 'module/CustomField/type/CFType';
import {log} from 'common/log';
import {SisName} from 'util/SisName';
import {Booking, Persurl} from 'generated/JavaTOmodifications';
import {BSModelIDPart} from 'module/BSCollection/type/BSCollectionStateType';
import {CustomFieldTO} from 'generated/JavaTO'
import {isEqualEventDialogModel} from 'module/EventDialog/util/isEqualEventDialogModel'

type ModelType = Partial<Booking | Persurl>
type BSRequestOptions = { filterCFDstFieldNames?: string[] }

const cfAPIKeys = ['persist', 'dstField', 'pos', 'preview']; // and lazy
function filterCFDataAttr(cfMember: CFMember | undefined) {
	return _.omit(cfMember, cfAPIKeys);
}

function getCFMember(cf: CFMember[], dstField: string, pos: number): CFMember | undefined {
	return _.find(cf, {dstField, pos});
}

const filterAndMarkChangedCF = (cf: CFMember[], persistedCF: CFMember[] = [], filterCFDstFieldNames?: string[]) => {
	// remove null values from cf
	cf = (cf || []).filter(m => Object.values(filterCFDataAttr(m)).filter(x => x || x === 0).length);
	persistedCF = persistedCF || [];
	// add remove requests for already persisted but now missing members
	const removeMissing = persistedCF.filter(m => !getCFMember(cf, m.dstField, m.pos)).map(({dstField, pos}) => ({persist: true, dstField, pos}));
	// add create requests for new and change requests for members that differ
	const changedCF = cf.reduce((result, cfMember) => {
			if (!cfMember) return result;
			else if (!cfMember.dstField) {
				throw new Error("dstField missing");
			} else if (typeof cfMember.pos !== 'number') {
				throw new Error("pos missing");
			} else if (_.isEqual(
				filterCFDataAttr(cfMember),
				filterCFDataAttr(getCFMember(persistedCF, cfMember.dstField, cfMember.pos)),
			)) {
				return result;
			} else {
				return result.concat({...cfMember, persist: true});
			}
		},
		removeMissing);
	return changedCF.filter(a => _.includes(filterCFDstFieldNames, a.dstField));
};

const askForConfirmationWhenNeeded = (model: ModelType | undefined, persistedModel: BSModelIDPart | ModelType | undefined, apiAction: BSApiAction) => {
	if (persistedModel) {
		let txtOnlyAction = null;
		switch (apiAction) {
			case BSApiAction.DELETE:
				txtOnlyAction = 'Löschen';
				break;
			case BSApiAction.ABL:
				txtOnlyAction = 'Ablehnen';
				break;
		}
		if (txtOnlyAction && !isEqualEventDialogModel(_.omit(model, "text"), _.omit(persistedModel as ModelType, 'text'))) {
			// only ask if action was user-initiated (not only changed in form)
			return confirmJN(
				`Änderung beim ${txtOnlyAction}`,
				`Beim ${txtOnlyAction} wird nur die Bemerkung gespeichert. Andere Änderungen werden ignoriert. Soll die Aktion ohne diese Änderungen durchgeführt werden?`,
			)
		}
	}
	return Promise.resolve();
};

function getRestAPIUrl(sisName: SisName) {
	let allUrls = SisNameAppSpecificUrl[getAppName()];
	if (!allUrls) allUrls = SisNameUrl;
	const url = allUrls[sisName];
	if (!url) throw new Error("Not yet implemented: " + sisName);
	return url;
}

function getEntityAttributes(sisName: SisName) {
	if (getAppName() === 'planTemplate') {
		return EntityAttributes[SisName.PlanTempl];
	}
	return EntityAttributes[sisName];
}


export const getBSEntryRequestParameters = (model: ModelType | undefined, persistedModel: BSModelIDPart | ModelType | undefined, restAction: BSApiAction, options?: BSRequestOptions) => {
	const {filterCFDstFieldNames} = options || {};
	model = _.cloneDeep(model);
	if (!model) model = {};
	if (model.cf) model.cf = filterAndMarkChangedCF(model.cf, (persistedModel as ModelType)?.cf ?? [], filterCFDstFieldNames)
	const sisName = model && model.sisName || persistedModel && persistedModel.sisName
	if (!sisName) throw new Error("SisName not defined!")
	let method: Method;
	let urlPart1 = getRestAPIUrl(sisName);
	let urlPart2;
	let urlPart3;
	let params = {};
	let data;
	switch (sisName) {
		case SisName.Persurl:
			if (persistedModel) {
				const {id} = persistedModel as Persurl;
				urlPart2 = encodeURIComponent(id);
			}
			break;
		case SisName.Booking:
			if (persistedModel) {
				const {bookingNr} = persistedModel as Booking;
				urlPart2 = encodeURIComponent(bookingNr);
			}
			break;
	}

	switch (restAction) {
		case BSApiAction.GET:
			method = 'GET'
			break;
		case BSApiAction.GEN:
			urlPart3 = 'gen'
		//fallthrough
		case BSApiAction.SAVE:
			if (persistedModel) method = 'PUT'
			else method = 'POST'
			const ea = getEntityAttributes(sisName)
			data = ea ? _.pick(model, ea) : model
			break;
		case BSApiAction.ABL:
			urlPart3 = 'abl'
		//fallthrough
		case BSApiAction.DELETE:
			method = 'DELETE'
			params = {...params, text: model.text}
			break;
	}

	const url = _.compact([urlPart1, urlPart2, urlPart3]).join('/');

	return {
		url,
		params,
		method,
		data,
	}
};

export function getAllCFDstFieldNames(customFieldDefinitions: CustomFieldTO[] = []) {
	return customFieldDefinitions
		.filter(cfDef => cfDef.dstField.startsWith("cf."))
		.map(cfDef => cfDef.dstField.substr(3));
}


/**
 *
 * @param model
 * @param persistedModel
 * @param bsAction
 * @param options
 * @param options.filterCFDstFieldNames required when writing to cf namespace (use getAllCFDstFieldNames)
 * @returns {*}
 */
export function doRestApiActionBSModel(
	model: ModelType | undefined,
	persistedModel: BSModelIDPart | ModelType | undefined,
	bsAction: BSApiAction,
	options?: BSRequestOptions,
) {
	return askForConfirmationWhenNeeded(model, persistedModel, bsAction).then(() => {
		const params = getBSEntryRequestParameters(model, persistedModel, bsAction, options);
		const req = axios.request(params).then(({data}) => data || null);
		req.catch(error => alertServerError(
				"Aktion fehlgeschlagen",
				_.get(error, ['response', 'data', 'message']) || "Fehler bei der Durchführung der Aktion",
			),
		);
		return req;
	});
}

const _fetchLazyCFAttributesRequests = {};
export const getCurrentCFAttributeRequest = (sisName: SisName, id: number | string, dstField: string, pos: number, fetchMode: FetchMode) => _.get(_fetchLazyCFAttributesRequests, [sisName, id, dstField, pos, fetchMode]);

export function fetchCFAttributeAPI(sisName: SisName, id: number | string, dstField: string, pos: number, fetchMode: FetchMode): Promise<CFMember> {
	const currentReq = getCurrentCFAttributeRequest(sisName, id, dstField, pos, fetchMode);
	if (currentReq) return currentReq;
	switch (sisName) {
		case ET_PERSURL:
		case ET_BOOKING:
			const req = axios.post<CFMember>('../json/attachment/get', {
				dstField,
				pos,
				id,
				sisName,
				fetchMode: fetchMode || FetchMode.EAGER,
			}).then(({data}) => data);
			_.set(_fetchLazyCFAttributesRequests, [sisName, id, dstField, pos, fetchMode], req);
			req.catch(error => void alertError(
				`Fehler beim Laden des erweiterten Attributs "${dstField}, Position: ${pos}"`,
			)).then(() => _.set(_fetchLazyCFAttributesRequests, [sisName, id, dstField, pos, fetchMode], undefined)); // remove from req cache
			return req;
		default :
			log.error("unknown sisName attachment fetch", sisName);
			throw new Error("Unbekannter Eintrag")
	}
}

export const apiStorno = (sisName: SisName, id: number, text: string, answer: boolean) =>
	axios.post('../json/storno', {sisName, id, text, answer});

export const _test = {filterAndMarkChangedCF};